import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useContent } from "@/lib/ContentProvider";

/**
 * Page d'entrée immersive avec continuité visuelle vers le Hub.
 *
 * 1. Vidéo autoplay (porte fermée → ouverte).
 * 2. Fin de vidéo : bouton "Rejoindre le refuge" apparaît en fondu.
 * 3. Clic : fondu doux vers une teinte crème/dorée (la lumière du seuil),
 *    puis navigation vers /accueil DANS LE MÊME ONGLET. Le Hub y émerge
 *    depuis cette même teinte, éliminant toute rupture visuelle.
 * 4. Ambiance sonore : tentative de lecture de /media/audio/ambience.mp3
 *    (vent + oiseaux) démarrée au clic. Fichier optionnel : si absent,
 *    l'expérience reste silencieuse sans erreur visible.
 */

const DOOR_VIDEO_MP4 = "/media/videos/entree-porte.mp4";
const DOOR_VIDEO_WEBM = "/media/videos/entree-porte.webm";
const DOOR_POSTER = "/media/images/entree-porte-poster.jpg";
const DEFAULT_DOOR_AUDIO = "/media/audio/ambience.mp3"; // fallback statique
const HUB_URL = "/accueil";
const WARM_LIGHT = "#f5e7c8"; // teinte dorée du seuil (à harmoniser avec le fond du Hub)

export default function Entry() {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [doorOpen, setDoorOpen] = useState(false);
  const [entering, setEntering] = useState(false);
  const navigate = useNavigate();
  const { content, mediaUrl } = useContent();
  // Son de la porte configurable depuis l'admin, avec fallback statique
  const customDoor = content?.siteSounds?.door;
  const doorAudioSrc = customDoor ? mediaUrl(customDoor) : DEFAULT_DOOR_AUDIO;

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    // Le bouton apparaît dès que la porte est visiblement en train de s'ouvrir
    // (35% de la vidéo) ou à la fin, en repli. Cela réduit fortement l'attente.
    const onTimeUpdate = () => {
      if (!el.duration || isNaN(el.duration)) return;
      if (el.currentTime / el.duration >= 0.35) setDoorOpen(true);
    };
    const onEnded = () => setDoorOpen(true);
    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("ended", onEnded);
    // Repli : si le navigateur ne déclenche pas timeupdate rapidement
    const fallback = setTimeout(() => setDoorOpen(true), 3500);
    return () => {
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("ended", onEnded);
      clearTimeout(fallback);
    };
  }, []);

  const openHub = async () => {
    if (entering) return;
    setEntering(true);
    // Sauvegarde un flag pour que le Hub sache animer son entrée
    try { sessionStorage.setItem("cr-entering-from-door", "1"); } catch (_) {}
    // Tentative de lancement du son ambiant (échec silencieux si le fichier
    // n'existe pas ou si le navigateur bloque)
    try {
      const a = audioRef.current;
      if (a) { a.volume = 0.4; await a.play(); }
    } catch (_) {}
    // Attente du fondu vers la teinte dorée (1.4 s), puis navigation
    setTimeout(() => navigate(HUB_URL), 1400);
  };

  return (
    <div
      data-testid="entry-page"
      className="fixed inset-0 w-full h-full overflow-hidden bg-black"
    >
      <video
        ref={videoRef}
        data-testid="entry-video"
        poster={DOOR_POSTER}
        autoPlay
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src={DOOR_VIDEO_WEBM} type="video/webm" />
        <source src={DOOR_VIDEO_MP4} type="video/mp4" />
      </video>

      {/* Élément audio pour l'ambiance (chargé mais joué au clic) */}
      <audio
        ref={audioRef}
        src={doorAudioSrc}
        loop
        preload="auto"
        data-testid="entry-ambience"
      />

      {/* Voile bas pour la lisibilité — s'estompe lors du passage */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.08) 45%, rgba(0,0,0,0.55) 100%)",
          opacity: entering ? 0 : 1,
          transition: "opacity 1000ms ease-in-out",
        }}
      />

      {/* Overlay chaud "lumière du seuil" — apparaît au clic pour la transition */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background: WARM_LIGHT,
          opacity: entering ? 1 : 0,
          transition: "opacity 1400ms ease-in-out",
        }}
      />

      {/* Texte + bouton */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center pb-14 md:pb-20 px-6 text-center">
        <p
          data-testid="entry-welcome"
          className="font-heading font-light text-white text-3xl md:text-5xl leading-[1.1] tracking-[0.01em] max-w-3xl"
          style={{
            textShadow: "0 2px 22px rgba(0,0,0,0.55)",
            opacity: entering ? 0 : 1,
            transition: "opacity 700ms ease-in-out",
          }}
        >
          Le refuge <span className="italic">vous attend</span>
        </p>

        <button
          type="button"
          onClick={openHub}
          data-testid="entry-cta"
          disabled={!doorOpen || entering}
          aria-hidden={!doorOpen}
          className="mt-8 md:mt-10 inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-white/70 bg-white/10 backdrop-blur-md text-white font-body text-[11px] md:text-xs uppercase transition-all hover:bg-white hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
          style={{
            letterSpacing: "0.24em",
            opacity: doorOpen && !entering ? 1 : 0,
            transform: doorOpen ? "translateY(0)" : "translateY(8px)",
            transition:
              "opacity 900ms ease-out, transform 1200ms ease-out, background-color 400ms ease, color 400ms ease, border-color 400ms ease",
            pointerEvents: doorOpen && !entering ? "auto" : "none",
          }}
        >
          Rejoindre le refuge
        </button>
      </div>
    </div>
  );
}
